// src/pages/inventory/products/ProductsPage.tsx
import React, { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import { Avatar, Badge, Image, Select, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Product, ProductCategory } from "../../../types/inventory/ProductTypes";
import { defaultProduct } from "../../../types/inventory/ProductTypes";
import { PictureOutlined, PlusOutlined } from "@ant-design/icons";
import EditProductDrawer from "./EditProductDrawer";
import { fetchProducts, updateProduct } from "../../../api/inventory/productApi";
import { fetchProductCategories } from "../../../api/inventory/productCategoryApi";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { useNotifications } from "../../../context/NotificationContext";
import { fetchClinicCompany } from "../../../api/admin/companyApi";
import { Company } from "../../../types/admin/CompanyTypes";
import { PageToolbar, DataTable, StatusTag, InlineLoading } from "../../../components/shared";
import { productImageUrl } from "../../../utils/inventory/productImageUrl";

const { Text } = Typography;

const ProductsPage: React.FC = () => {
	const [products, setProducts] = useState<Product[]>([]);
	const [categories, setCategories] = useState<ProductCategory[]>([]);
	const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
	const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [sortField, setSortField] = useState<string>("created_at");
	const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | undefined>("descend");
	const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<number | undefined>(undefined);
	const [searchQuery, setSearchQuery] = useState<string>("");

	const PRODUCTS_PER_PAGE = loadUserPreferences().tableRowsPerPage;
	const { showError, showSuccess } = useNotifications();
	const [company, setCompany] = useState<Company | null>(null);

	useEffect(() => {
		const loadCompany = async () => {
			const c = await fetchClinicCompany();
			setCompany(c);
		};
		loadCompany();
	}, []);

	useEffect(() => {
		if (company) {
			loadCategories();
			loadAllProducts();
		}
	}, [company]);

	useEffect(() => {
		applyFilters();
	}, [products, selectedCategoryFilter, searchQuery]);

	const loadCategories = async () => {
		try {
			const { data } = await fetchProductCategories(1, 100, company!.id, "", "commerce");
			setCategories(data.filter((cat) => cat.is_active));
		} catch {
			showError("Failed to load categories");
		}
	};

	const loadAllProducts = async () => {
		setIsLoading(true);
		try {
			const allProducts: Product[] = [];
			let page = 1;
			let hasMore = true;

			while (hasMore) {
				const { data, total } = await fetchProducts(page, 100, company!.id, "", undefined);
				allProducts.push(...data);
				hasMore = allProducts.length < total;
				page++;
			}

			setProducts(allProducts);
		} catch {
			showError("Failed to load products");
		} finally {
			setIsLoading(false);
		}
	};

	const applyFilters = () => {
		let filtered = [...products];

		if (selectedCategoryFilter) {
			filtered = filtered.filter((p) => p.product_category_id === selectedCategoryFilter);
		}

		if (searchQuery) {
			const q = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(p) =>
					p.name.toLowerCase().includes(q) ||
					p.sku.toLowerCase().includes(q) ||
					p.description?.toLowerCase().includes(q) ||
					p.barcode?.toLowerCase().includes(q)
			);
		}

		setFilteredProducts(filtered);
	};

	const handleCategoryChange = async (productId: number, newCategoryId: number) => {
		try {
			await updateProduct(productId, { product_category_id: newCategoryId });
			showSuccess("Category updated successfully");
			loadAllProducts();
		} catch {
			showError("Failed to update category");
		}
	};

	const onNewProduct = () => {
		setSelectedProduct({ ...defaultProduct, company_id: company!.id });
		setIsDrawerOpen(true);
	};

	const handleEditClick = (product: Product) => {
		setSelectedProduct(product);
		setIsDrawerOpen(true);
	};

	const handleDrawerClose = async () => {
		setSelectedProduct(null);
		setIsDrawerOpen(false);
		await loadAllProducts();
	};

	const handleReset = () => {
		setSearchQuery("");
		setSelectedCategoryFilter(undefined);
		setCurrentPage(1);
	};

	const paginatedProducts = filteredProducts.slice(
		(currentPage - 1) * PRODUCTS_PER_PAGE,
		currentPage * PRODUCTS_PER_PAGE
	);

	const columns: ColumnsType<Product> = [
		{
			title: "",
			key: "image",
			width: 80,
			render: (_: unknown, record: Product) => {
				const primaryImage = record.images?.find((img) => img.is_primary) || record.images?.[0];
				return primaryImage ? (
					<div onClick={(e) => e.stopPropagation()}>
						<Image
							src={productImageUrl(primaryImage.file_id)}
							alt={primaryImage.alt_text || record.name}
							width={60}
							height={60}
							style={{ objectFit: "cover", borderRadius: 8 }}
						/>
					</div>
				) : (
					<Avatar
						shape="square"
						size={60}
						icon={<PictureOutlined />}
						style={{ background: "var(--mg-bg-subtle)", color: "var(--mg-text-secondary)" }}
					/>
				);
			},
		},
		{
			title: "Product",
			key: "name",
			sorter: true,
			sortOrder: sortField === "name" ? sortOrder : undefined,
			render: (_: unknown, record: Product) => <Text strong>{record.name}</Text>,
		},
		{
			title: "Category",
			key: "category",
			width: 160,
			render: (_: unknown, record: Product) => (
				<Text type="secondary">{record.category?.name || "Uncategorized"}</Text>
			),
		},
		{
			title: "Status",
			key: "status",
			width: 120,
			render: (_: unknown, record: Product) => (
				<StatusTag status={record.status || "unlisted"} label={record.status || "Unlisted"} />
			),
		},
		{
			title: "Price",
			dataIndex: "unit_price",
			key: "unit_price",
			sorter: true,
			sortOrder: sortField === "unit_price" ? sortOrder : undefined,
			width: 110,
			align: "right",
			render: (value: unknown) => {
				const n = Number(value);
				return n > 0 ? <Text>${n.toFixed(2)}</Text> : <Text type="secondary">—</Text>;
			},
		},
		{
			title: "Cost",
			dataIndex: "unit_cost",
			key: "unit_cost",
			sorter: true,
			sortOrder: sortField === "unit_cost" ? sortOrder : undefined,
			width: 100,
			align: "right",
			render: (value: unknown) => {
				const n = Number(value);
				return n > 0 ? <Text>${n.toFixed(2)}</Text> : <Text type="secondary">—</Text>;
			},
		},
		{
			title: "Margin",
			key: "margin",
			width: 110,
			align: "right",
			render: (_: unknown, record: Product) => {
				const price = Number(record.unit_price);
				const cost = Number(record.unit_cost);
				if (price > 0 && cost > 0) {
					const margin = price - cost;
					const pct = (margin / price) * 100;
					return (
						<Space direction="vertical" size={0} style={{ textAlign: "right" }}>
							<Text strong style={{ color: margin > 0 ? "var(--mg-success)" : "var(--mg-danger)" }}>
								${margin.toFixed(2)}
							</Text>
							<Text type="secondary" style={{ fontSize: 11 }}>
								{pct.toFixed(1)}%
							</Text>
						</Space>
					);
				}
				return <Text type="secondary">—</Text>;
			},
		},
		{
			title: "Stock",
			key: "stock",
			width: 130,
			align: "center",
			render: (_: unknown, record: Product) => {
				let total = 0;
				if (record.variants?.length) {
					total = record.variants.reduce(
						(sum, v) => sum + (v.stock?.reduce((s, st) => s + (st.quantity || 0), 0) || 0),
						0
					);
				} else if (record.stock) {
					total = record.stock.reduce((sum, s) => sum + (s.quantity || 0), 0);
				}
				if (!total) return <Badge status="error" text="Out of Stock" />;
				if (total < 10) return <Badge status="warning" text={`${total} units`} />;
				return <Badge status="success" text={`${total} units`} />;
			},
		},
		{
			title: "Variants",
			key: "variants",
			width: 90,
			align: "center",
			render: (_: unknown, record: Product) => {
				const count = record.variants?.length || 0;
				return count > 0 ? (
					<Badge count={count} showZero style={{ backgroundColor: "var(--mg-primary)" }} />
				) : (
					<Text type="secondary">—</Text>
				);
			},
		},
	];

	return (
		<Layout>
			<PageToolbar
				primaryAction={{ label: "New Product", icon: <PlusOutlined />, onClick: onNewProduct }}
				filters={[
					{
						type: "search",
						placeholder: "Search by name, SKU, or barcode…",
						value: searchQuery,
						onChange: (v) => {
							setSearchQuery(v);
							setCurrentPage(1);
						},
						width: 280,
					},
					{
						type: "custom",
						render: (
							<Select
								placeholder="Filter by category"
								allowClear
								style={{ width: 200 }}
								value={selectedCategoryFilter}
								onChange={(v) => {
									setSelectedCategoryFilter(v);
									setCurrentPage(1);
								}}
								options={categories.map((cat) => ({ label: cat.name, value: cat.id }))}
							/>
						),
					},
				]}
				onReset={handleReset}
			/>

			{isLoading ? (
				<InlineLoading message="Loading products…" minHeight={300} />
			) : (
				<DataTable
					columns={columns}
					dataSource={paginatedProducts}
					rowKey="product_id"
					loading={isLoading}
					total={filteredProducts.length}
					currentPage={currentPage}
					pageSize={PRODUCTS_PER_PAGE}
					onPageChange={(page) => setCurrentPage(page)}
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={(field, order) => {
						setSortField(field);
						setSortOrder(order);
						setCurrentPage(1);
					}}
					onRowClick={handleEditClick}
					emptyTitle="No products found"
					emptyDescription="Create your first product or adjust your search filters"
					fillHeight
				/>
			)}

			{selectedProduct && (
				<EditProductDrawer isOpen={isDrawerOpen} onClose={handleDrawerClose} product={selectedProduct} />
			)}
		</Layout>
	);
};

export default ProductsPage;
